import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import ListingCard from "../components/ListingCard";
import { ArrowRight, Sparkles } from "lucide-react";

export default function Landing() {
  const { t } = useI18n();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/listings?limit=8").then(r => setFeatured(r.data)).catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 space-y-8">
            <div className="overline text-[#143122] flex items-center gap-2">
              <Sparkles size={12} strokeWidth={1.5} /> {t("tagline")}
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#143122]">
              {t("hero_title")}
            </h1>
            <p className="text-base md:text-lg text-[#1A1F1C]/75 max-w-xl leading-relaxed">
              {t("hero_sub")}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link data-testid="hero-cta-signup" to="/register" className="kav-btn-primary">
                {t("get_started")} <ArrowRight size={16} strokeWidth={1.75} />
              </Link>
              <Link data-testid="hero-cta-browse" to="/marketplace" className="kav-btn-secondary">
                {t("browse")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="aspect-[4/5] w-full bg-[#143122] rounded-lg overflow-hidden grain-overlay">
                <img
                  src="https://images.pexels.com/photos/13340055/pexels-photo-13340055.jpeg"
                  alt="Kavioura hero"
                  className="w-full h-full object-cover opacity-90"
                />
              </div>
              <div className="hidden md:block absolute -bottom-6 -left-6 bg-[#F4F1E9] border border-[#E6E2D8] p-5 rounded-lg max-w-[240px]">
                <div className="overline text-[#143122]">Nordic</div>
                <div className="font-serif-display text-2xl mt-1 text-[#1A1F1C] leading-tight">
                  Trusted by riders across FI · SV · EN
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section-linen py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="overline text-[#143122]">Overview</div>
            <h2 className="mt-3 font-serif-display text-4xl md:text-5xl text-[#143122] leading-tight">
              {t("how_it_works")}
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {[
              { n: "01", t: t("step1_title"), d: t("step1_desc") },
              { n: "02", t: t("step2_title"), d: t("step2_desc") },
              { n: "03", t: t("step3_title"), d: t("step3_desc") },
            ].map((s, i) => (
              <div key={s.n} data-testid={`step-${i + 1}`} className="bg-white border border-[#E6E2D8] p-8 rounded-lg">
                <div className="font-serif-display text-5xl text-[#D1C6B4]">{s.n}</div>
                <div className="mt-4 font-serif-display text-2xl text-[#143122]">{s.t}</div>
                <p className="mt-3 text-sm text-[#1A1F1C]/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline text-[#143122]">{t("featured")}</div>
              <h2 className="mt-2 font-serif-display text-4xl text-[#143122]">
                {t("featured")}
              </h2>
            </div>
            <Link data-testid="see-all-listings" to="/marketplace" className="text-sm text-[#143122] hover:underline">
              {t("browse")} →
            </Link>
          </div>
          {featured.length === 0 ? (
            <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-16 text-center">
              No listings yet — <Link to="/register" className="underline">be the first to post.</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.slice(0, 8).map((l, i) => (
                <ListingCard key={l.id} listing={l} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
